/**
 * Records a version of the ICS-201 whenever any of its boxes saves.
 *
 * The card is assembled from components that belong to other cards, so there is
 * no one mutation to hang this off. Boxes the card owns call the recorder
 * directly; the reused ones — objectives, actions, resource sets, safety
 * messages — reach it from their own mutation hooks, which read this context
 * and find a no-op everywhere else.
 *
 * `ICS_201` has no `incidentInfoSources` mapping on the server, so each
 * recorded save appends a row rather than folding into the last one. For a form
 * whose whole purpose is to hand over what was known at a moment, that history
 * is the point.
 */
import React, { useMemo } from 'react';

import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

import { RecordIcs201VersionContext } from './ics201VersionRecordingContext';

export { useRecordIcs201Version } from './ics201VersionRecordingContext';

export const Ics201VersionRecording: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { effectivePeriodNumber, readOnlyForms } = useICSContext();
  const recordVersion = useRecordFormVersionSave(
    'ICS_201',
    effectivePeriodNumber
  );

  const record = useMemo(
    () => () => {
      if (!readOnlyForms) recordVersion.mutate();
    },
    [readOnlyForms, recordVersion]
  );

  return (
    <RecordIcs201VersionContext.Provider value={record}>
      {children}
    </RecordIcs201VersionContext.Provider>
  );
};

export default Ics201VersionRecording;
